import { Badge, Card, CardContent, CardHeader, CardTitle } from "@/ui";
import { 
  FileText, 
  Mic, 
  Clock, 
  TrendingDown, 
  CheckCircle,
  Brain
} from "lucide-react";
const cliniciansImage = "/assets/website/images/Clinicians_using_AI_tools_b789fe10.png";

const clinicianSolutions = [
  {
    icon: Brain,
    title: "Clinical Decision Support",
    description: "Context-aware guidance with evidence-based recommendations",
    features: [
      "Real-time diagnostic assistance",
      "Drug interaction alerts",
      "Care pathway suggestions",
      "Risk stratification"
    ],
    kpis: [
      { label: "Alert Relevance", value: "95%" },
      { label: "Clinical Accuracy", value: "98%" }
    ]
  },
  {
    icon: Mic,
    title: "Ambient Listening & Scribe",
    description: "Hands-free documentation with intelligent conversation capture",
    features: [
      "Real-time transcription",
      "Structured note generation",
      "ICD-10 coding assistance",
      "Clinician review workflow"
    ],
    kpis: [
      { label: "Documentation Time", value: "-70%" },
      { label: "Note Quality Score", value: "92%" }
    ]
  },
  {
    icon: FileText,
    title: "ChartPrep Automation",
    description: "Pre-visit chart review and intelligent summarization",
    features: [
      "Patient history synthesis",
      "Care gap identification",
      "Priority condition flagging",
      "Prep time optimization"
    ],
    kpis: [
      { label: "Prep Time Reduction", value: "80%" },
      { label: "Care Gap Detection", value: "95%" }
    ]
  }
];

const workflowBenefits = [
  {
    icon: Clock,
    title: "Time Savings",
    description: "Average 2.5 hours saved per clinician per day",
    improvement: "50% reduction in administrative tasks"
  },
  {
    icon: TrendingDown,
    title: "Reduced Burnout",
    description: "Measurable improvement in clinician satisfaction",
    improvement: "30% decrease in burnout indicators"
  },
  {
    icon: CheckCircle,
    title: "Quality Improvement",
    description: "Enhanced care quality through intelligent guidance",
    improvement: "15% improvement in quality metrics"
  }
];

export default function SolutionsClinicians() {

  return (
    <section id="clinicians" className="py-20">
      {/* Hero Image Section */}
      <div className="relative h-80 mb-20 overflow-hidden">
        <img 
          src={cliniciansImage} 
          alt="Clinicians Using AI Tools"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background/95 via-background/80 to-background/60" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Badge variant="secondary" className="mb-4" data-testid="badge-clinicians">
              Solutions for Clinicians
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-4" data-testid="text-clinicians-title">
              Intelligent Assistants for Every Workflow
            </h2>
            <p className="text-xl text-foreground max-w-3xl mx-auto" data-testid="text-clinicians-description">
              Reduce administrative burden and enhance clinical decision-making with AI agents designed 
              specifically for healthcare workflows.
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto max-w-7xl bg-muted/30 py-16 rounded-lg">

        {/* Solutions Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16 place-items-center justify-items-center">
          {clinicianSolutions.map((solution, index) => (
            <Card key={index} className="hover-elevate" data-testid={`card-clinician-solution-${index}`}>
              <CardHeader>
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-lg">
                    <solution.icon className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <CardTitle className="text-xl" data-testid={`text-solution-title-${index}`}>
                  {solution.title}
                </CardTitle>
                <p className="text-muted-foreground" data-testid={`text-solution-description-${index}`}>
                  {solution.description}
                </p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Features */}
                <div>
                  <h4 className="font-semibold mb-3 text-sm">Key Features</h4>
                  <ul className="space-y-2">
                    {solution.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-sm text-muted-foreground" data-testid={`text-feature-${index}-${featureIndex}`}>
                        <div className="w-1.5 h-1.5 bg-primary rounded-full mr-3 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* KPIs */}
                <div>
                  <h4 className="font-semibold mb-3 text-sm">
                    Impact Metrics<span className="text-red-500">*</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    {solution.kpis.map((kpi, kpiIndex) => (
                      <div key={kpiIndex} className="text-center p-2 bg-muted/50 rounded" data-testid={`kpi-${index}-${kpiIndex}`}>
                        <div className="text-lg font-bold text-primary" data-testid={`kpi-value-${index}-${kpiIndex}`}>
                          {kpi.value}
                        </div>
                        <div className="text-xs text-muted-foreground" data-testid={`kpi-label-${index}-${kpiIndex}`}>
                          {kpi.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </CardContent>
            </Card>
          ))}
        </div>

        {/* Workflow Benefits */}
        <div className="bg-background rounded-lg p-8 mb-12 max-w-6xl mx-auto">
          <h3 className="text-2xl font-bold text-center mb-8" data-testid="text-benefits-title">
            Workflow Transformation Benefits
          </h3>
          <div className="grid md:grid-cols-3 gap-6 place-items-center justify-items-center">
            {workflowBenefits.map((benefit, index) => (
              <div key={index} className="text-center" data-testid={`benefit-${index}`}>
                <div className="flex items-center justify-center w-16 h-16 bg-accent/20 rounded-lg mx-auto mb-4">
                  <benefit.icon className="h-8 w-8 text-accent-foreground" />
                </div>
                <h4 className="font-semibold text-foreground mb-2" data-testid={`benefit-title-${index}`}>
                  {benefit.title}
                </h4>
                <p className="text-sm text-muted-foreground mb-3" data-testid={`benefit-description-${index}`}>
                  {benefit.description}
                </p>
                <Badge variant="secondary" className="text-xs" data-testid={`benefit-improvement-${index}`}>
                  {benefit.improvement}
                </Badge>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
}